import React, { ReactNode, useCallback, useMemo } from 'react';

import { Input, InputEvent, InputProps } from '@/components/form/Input';
import { Option } from '@/components/form/Select';

export type Values = Record<string, InputProps['value']>;
type InputType = Omit<InputProps, 'onChange'>;
export type InputsT = (InputType & { children?: ReactNode })[];

interface Props {
  className?: string;
  inputs: InputsT;
  onChange: (values: Values) => void;
  values?: Values;
  defaultValues?: Values;
}

// eslint-disable-next-line react-refresh/only-export-components
export const getFormValues = (inputs: InputsT): Values => {
  if (!Array.isArray(inputs)) {
    return {};
  }

  return inputs.reduce((acc, current) => {
    if (current.children) {
      return {
        ...acc,
        ...getFormValues(current.children as InputsT),
      };
    }
    return {
      ...acc,
      [current.name]: current.value,
    };
  }, {} as Values);
};

export const Inputs: React.FC<Props> = ({
  className,
  inputs,
  onChange,
  values,
  defaultValues,
}: Props) => {
  // Compute current values: prefer props.values, fall back to defaultValues, then input defaults
  const currentValues = useMemo(() => {
    const base = defaultValues || getFormValues(inputs);
    return values ? { ...base, ...values } : base;
  }, [values, defaultValues, inputs]);

  const handleChange = useCallback(
    (input: InputType) => {
      return function (event: InputEvent) {
        const options: Option[] =
          input.type === Input.Type.SELECT && input.options
            ? input.options
            : [];
        const isNumber = options.some(({ value }) => typeof value === 'number');
        const { name, value } = event.target;

        onChange({
          ...currentValues,
          [name]: isNumber ? parseInt(value, 10) : value,
        });
      };
    },
    [currentValues, onChange]
  );

  return (
    <>
      {inputs.map(input => {
        if (input.children) {
          return (
            <React.Fragment key={input.name}>{input.children}</React.Fragment>
          );
        }

        return (
          <Input
            {...input}
            className={className}
            key={input.name}
            value={currentValues[input.name] ?? ''}
            onChange={handleChange(input)}
          />
        );
      })}
    </>
  );
};
